import { FieldsConfigResponse } from "@hooks/use-config-context/types";

/**
 * Builds a flat map of sub-field configs keyed by sub-field id,
 * each annotated with the parent field id.
 * Shared by formatAssetForSave and formatTicketForSave.
 */
export const buildSubFieldsConfig = (
  config: FieldsConfigResponse,
): Record<string, any> =>
  Object.values(config)
    .filter((field) => (field.fields?.length ?? 0) > 0)
    .reduce(
      (acc, field) => {
        field.fields?.forEach((subField) => {
          acc[subField.id] = {
            parentFieldId: field.id,
            ...subField,
          };
        });
        return acc;
      },
      {} as Record<string, any>,
    );

/**
 * Formats an ISO 8601 datetime string into a human-readable date string.
 * @param date - ISO 8601 datetime string (e.g., "2024-05-25 10:30:00")
 * @returns Formatted date string (e.g., "Sat, May 25, 2024")
 */
export const formatDate = (date?: string | null): string => {
  if (!date || typeof date !== "string") return "—";

  const d = parseDateInput(date);

  if (!d) return "—";

  const formattedDate = d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return formattedDate;
};

/**
 * Safely parses API/UI date strings into Date objects.
 * Date-only values (YYYY-MM-DD) are parsed as local calendar dates
 * to avoid timezone-based day rollback.
 */
export const parseDateInput = (date?: string | null): Date | null => {
  if (!date || typeof date !== "string") return null;

  const trimmed = date.trim();

  const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]) - 1;
    const day = Number(dateOnlyMatch[3]);
    const localDate = new Date(year, month, day);
    return Number.isNaN(localDate.getTime()) ? null : localDate;
  }

  const iso = trimmed.replace(" ", "T");
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};
